<script setup>
const props = defineProps(["product"]);

import { onMounted, ref, computed, watch } from "vue";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { prefersReducedMotion } from "@utils/prefersReducedMotion";
import { useShopifyStore } from "@stores/shopify.js";
import Button from "@/components/global/Button.vue";
import ArrowLeft from "@/components/icons/ArrowLeft.vue";
import Link from "@/components/icons/Link.vue";
import Tick from "@/components/icons/Tick.vue";
import Plus from "@/components/icons/Plus.vue";
import Minus from "@/components/icons/Minus.vue";
import ChevronUp from "@/components/icons/ChevronUp.vue";
import TransitionExpand from "@/components/transitions/TransitionExpand.vue";
import Lightbox from "@/components/global/Lightbox.vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";

const shopify = useShopifyStore();
const copied = ref(false);
const benefitsExpanded = ref(true);
const quantity = ref(1);
const selectedOptions = ref({});
const addingToCart = ref(false);
const lightboxOpen = ref(false);
const lightboxIndex = ref(0);

onMounted(() => {
	console.log(props.product);
	if (props.product.options) {
		props.product.options.forEach((option) => {
			if (option.values && option.values.length > 0) {
				selectedOptions.value[option.name] = option.values[0];
			}
		});
	}
});

const selectedVariant = computed(() => {
	if (!props.product.variants) return null;

	return props.product.variants.find((variant) => {
		return variant.selectedOptions.every(
			(option) => selectedOptions.value[option.name] === option.value,
		);
	});
});

const getSwatchForOption = (optionName, optionValue) => {
	if (!props.product.variants) return null;

	const variant = props.product.variants.find((variant) => {
		return variant.selectedOptions.every((option) => {
			if (option.name === optionName) {
				return option.value === optionValue;
			}
			return selectedOptions.value[option.name] === option.value;
		});
	});

	return variant?.swatch || null;
};

const addToCart = async () => {
	if (!selectedVariant.value || !selectedVariant.value.available) {
		return;
	}

	addingToCart.value = true;

	try {
		await shopify.addToCart({
			id: selectedVariant.value.id,
			quantity: quantity.value,
		});
	} catch (error) {
		console.error("Failed to add to cart:", error);
	} finally {
		addingToCart.value = false;
	}
};

const copyLink = () => {
	navigator.clipboard.writeText(window.location.href);
	copied.value = true;
	setTimeout(() => {
		copied.value = false;
	}, 2000);
};

const openLightbox = (index) => {
	lightboxIndex.value = index;
	lightboxOpen.value = true;
};

const closeLightbox = () => {
	lightboxOpen.value = false;
};

onMounted(() => {
	if (prefersReducedMotion()) return;

	gsap.registerPlugin(ScrollTrigger);

	gsap.from("[animate-product-breadcrumbs]", {
		y: 12,
		opacity: 0,
		duration: 0.8,
		stagger: 0.1,
		ease: "power2.inOut",
	});

	gsap.from("[animate-product-image]", {
		css: {
			clipPath: "inset(0% 0% 0% 100%)",
			opacity: 0,
			transform: "translateY(24px) scale(0.9)",
		},
		duration: 0.8,
		stagger: 0.1,
		ease: "power2.inOut",
	});

	gsap.from("[animate-product-info]", {
		css: {
			clipPath: "inset(0% 0% 0% 100%)",
		},
		duration: 0.8,
		delay: 0.2,
		ease: "power2.inOut",
	});
});

watch(lightboxOpen, (newVal) => {
	if (newVal) {
		document.body.style.overflow = "hidden";
	} else {
		document.body.style.overflow = "";
	}
});
</script>

<template>
	<div class="container">
		<ul
			class="h8 font-primary-condensed mb-32 hidden items-center gap-16 lg:flex"
			animate-product-breadcrumbs
		>
			<li
				class="flex items-center gap-12 text-[var(--theme-accent)] after:text-[var(--theme-surface)]/50 after:content-['/']"
			>
				<router-link
					class="flex items-center gap-4 transition-opacity hover:opacity-50"
					:to="{ name: 'index' }"
				>
					<ArrowLeft />
					<p>See all teams</p>
				</router-link>
			</li>
			<li
				class="flex items-center gap-12 after:text-[var(--theme-surface)]/50 after:content-['/']"
			>
				<router-link
					class="flex items-center gap-4 transition-opacity hover:opacity-50"
					:to="{
						name: 'collection',
						params: { slug: product.parent_collection.slug },
					}"
				>
					<p>{{ product.parent_collection.title }}</p>
				</router-link>
			</li>
			<li class="flex items-center gap-12">
				<div class="flex items-center gap-4" :to="{ name: 'index' }">
					<p>{{ product.title }}</p>
				</div>
			</li>
		</ul>
		<div
			class="grid grid-cols-12 items-start gap-y-16 lg:gap-x-32 lg:gap-y-0"
		>
			<div
				class="col-span-12 grid grid-cols-2 gap-8 lg:col-span-6 xl:col-span-7"
			>
				<div class="col-span-2 mb-8 flex flex-col gap-16 lg:hidden">
					<div
						class="flex flex-wrap items-center justify-between lg:hidden"
					>
						<div
							class="flex items-center gap-12"
							v-if="product.parent_collection"
						>
							<img
								class="h-20"
								v-if="product.parent_collection.logo"
								:src="product.parent_collection.logo"
								alt=""
							/>
							<p
								class="h9 font-primary-condensed rounded-lg bg-[var(--theme-surface)]/10 p-8 uppercase"
							>
								{{ product.parent_collection.title }}
							</p>
						</div>
						<button
							class="h9 font-primary-condensed flex items-center gap-8 opacity-50 transition-opacity hover:opacity-100"
							:class="{ 'opacity-100': copied }"
							@click="copyLink"
						>
							<p>{{ copied ? "Copied!" : "Share" }}</p>
							<Link v-if="!copied" class="size-16" />
							<Tick v-else class="size-16" />
						</button>
					</div>
					<div class="flex flex-col gap-16 lg:hidden">
						<h1 class="md:h3 lg:h4 h5 font-primary-condensed">
							{{ product.title }}
						</h1>
						<ul
							class="h9 font-primary-condensed flex items-center gap-4 !text-[10px] lg:hidden"
							animate-product-breadcrumbs
						>
							<li
								class="flex items-center gap-4 text-[var(--theme-accent)] after:text-[var(--theme-surface)]/50 after:content-['/']"
							>
								<router-link
									class="flex items-center gap-4 transition-opacity hover:opacity-50"
									:to="{ name: 'index' }"
								>
									<ArrowLeft class="size-10" />
									<p>See all teams</p>
								</router-link>
							</li>
							<li
								class="flex items-center gap-4 after:text-[var(--theme-surface)]/50 after:content-['/']"
							>
								<router-link
									class="flex items-center gap-4 transition-opacity hover:opacity-50"
									:to="{
										name: 'collection',
										params: {
											slug: product.parent_collection.slug,
										},
									}"
								>
									<p>{{ product.parent_collection.title }}</p>
								</router-link>
							</li>
							<li class="flex items-center gap-4">
								<div
									class="flex items-center gap-4"
									:to="{ name: 'index' }"
								>
									<p>{{ product.title }}</p>
								</div>
							</li>
						</ul>
					</div>
				</div>
				<img
					animate-product-image
					class="col-span-2 aspect-video w-full cursor-pointer rounded-lg object-cover"
					:src="selectedVariant?.image ?? product.images[0]"
					@click="openLightbox(0)"
					alt="Product image"
				/>
				<img
					animate-product-image
					v-for="(image, index) in selectedVariant?.gallery.slice(1) ??
					product.images.slice(1)"
					:key="index + 1"
					class="aspect-square w-full cursor-pointer rounded-lg object-cover"
					:src="image"
					@click="openLightbox(index + 1)"
					:alt="`Product image ${index + 2}`"
				/>
				<!-- <div class="col-span-2 w-full lg:hidden">
					<Swiper class="overflow-hidden rounded-lg">
						<SwiperSlide
							v-for="(image, index) in selectedVariant?.gallery ??
							product.images"
							:key="index"
						>
							<img
								class="aspect-square w-full cursor-pointer object-cover transition-transform"
								:src="image"
								@click="openLightbox(index)"
							/>
						</SwiperSlide>
					</Swiper>
				</div> -->
			</div>
			<div
				class="col-span-12 flex flex-col items-start gap-32 lg:col-span-6 lg:gap-40 xl:col-span-5"
				animate-product-info
			>
				<div class="flex w-full flex-col gap-24">
					<div
						class="hidden flex-wrap items-center justify-between lg:flex"
					>
						<div
							class="flex items-center gap-20"
							v-if="product.parent_collection"
						>
							<img
								class="h-25"
								v-if="product.parent_collection.logo"
								:src="product.parent_collection.logo"
								alt=""
							/>
							<p
								class="h8 font-primary-condensed rounded-lg bg-[var(--theme-surface)]/10 p-12 uppercase"
							>
								{{ product.parent_collection.title }}
							</p>
						</div>
						<button
							class="h8 font-primary-condensed flex items-center gap-8 opacity-50 transition-opacity hover:opacity-100"
							:class="{ 'opacity-100': copied }"
							@click="copyLink"
						>
							<p>{{ copied ? "Copied!" : "Share" }}</p>
							<Link v-if="!copied" class="size-16" />
							<Tick v-else class="size-16" />
						</button>
					</div>
					<div class="flex flex-col gap-32">
						<h1
							class="md:h3 lg:h4 h5 font-primary-condensed hidden lg:block"
						>
							{{ product.title }}
						</h1>
						<div class="space-y-[1ch] text-sm" v-if="product.description">
							<p>
								{{ product.description }}
							</p>
						</div>
					</div>
				</div>

				<ul class="flex flex-col gap-32">
					<li
						class="flex w-full flex-col gap-16"
						v-for="(option, index) in product.options"
						:key="index"
					>
						<p class="h8 font-primary-condensed">
							{{ option.name }}
						</p>
						<div
							v-if="
								option.name === 'Style' ||
								option.name === 'Lens Designs'
							"
							class="flex flex-wrap gap-12"
						>
							<button
								v-for="value in option.values"
								:key="value"
								class="size-64 rounded-2xl border-2 bg-[var(--theme-surface)]/10 p-8 transition-all hover:bg-[var(--theme-surface)]/20 lg:size-74"
								:class="{
									'border-[var(--theme-surface)]/50':
										selectedOptions[option.name] === value,
									'border-transparent':
										selectedOptions[option.name] !== value,
								}"
								@click="selectedOptions[option.name] = value"
							>
								<img
									v-if="getSwatchForOption(option.name, value)"
									:src="getSwatchForOption(option.name, value)"
									:alt="value"
									class="size-full rounded-lg object-cover"
								/>
							</button>
						</div>

						<div v-else class="flex flex-wrap gap-12">
							<button
								v-for="value in option.values"
								:key="value"
								class="rounded-lg border-2 px-16 py-8 text-sm transition-all"
								:class="{
									'border-[var(--theme-surface)] bg-[var(--theme-surface)] text-[var(--theme-background)]':
										selectedOptions[option.name] === value,
									'border-[var(--theme-surface)]/20':
										selectedOptions[option.name] !== value,
								}"
								@click="selectedOptions[option.name] = value"
							>
								{{ value }}
							</button>
						</div>
					</li>
				</ul>

				<div class="w-full" v-if="product.benefits.length > 0">
					<button
						class="flex w-full items-center justify-between"
						@click="benefitsExpanded = !benefitsExpanded"
					>
						<h2 class="h8 font-primary-condensed">Benefits</h2>
						<div
							class="shrink-0 transition-transform duration-500"
							:class="{ 'size-16 rotate-180': benefitsExpanded }"
						>
							<ChevronUp />
						</div>
					</button>
					<TransitionExpand>
						<div v-if="benefitsExpanded && product.benefits.length > 0">
							<div class="pt-20">
								<ul
									class="grid gap-x-40 gap-y-16 text-sm sm:grid-cols-2"
								>
									<li
										class="flex items-center gap-16"
										v-for="benefit in product.benefits"
										:key="benefit"
									>
										<Tick class="size-12" />
										<p>{{ benefit }}</p>
									</li>
								</ul>
							</div>
						</div>
					</TransitionExpand>
				</div>
				<div
					class="flex w-full flex-col gap-y-16 rounded-lg bg-[var(--theme-surface)]/10 p-12 lg:flex-row lg:items-center lg:justify-between lg:gap-x-64"
				>
					<div class="flex flex-col gap-4">
						<p
							class="text-sm line-through opacity-50"
							v-if="selectedVariant?.compare_at_price"
						>
							{{ selectedVariant.compare_at_price }}
						</p>
						<p class="h6 font-primary">
							{{ selectedVariant?.price || product.price }}
						</p>
					</div>
					<div
						class="grid w-full grid-cols-2 gap-8 lg:flex lg:w-auto lg:gap-12"
					>
						<div class="flex rounded-lg bg-[var(--theme-background)] p-2">
							<button
								class="transition-alls flex size-48 items-center justify-center rounded-lg not-disabled:hover:bg-white/5 disabled:opacity-50"
								@click="quantity > 1 && quantity--"
								:disabled="quantity === 1"
								aria-label="Decrease quantity"
							>
								<Minus class="size-16" />
							</button>
							<div
								class="flex h-48 w-40 grow items-center justify-center lg:grow-0"
							>
								<span class="h8 font-primary-condensed">
									{{ quantity }}
								</span>
							</div>
							<button
								class="flex size-48 items-center justify-center rounded-lg transition-colors hover:bg-white/5"
								@click="quantity++"
								aria-label="Increase quantity"
							>
								<Plus class="size-16" />
							</button>
						</div>
						<Button
							:disabled="!selectedVariant?.available || addingToCart"
							:class="{
								'cursor-not-allowed opacity-50':
									!selectedVariant?.available,
							}"
							@click="addToCart"
						>
							{{ addingToCart ? "Adding..." : "Add to cart" }}
						</Button>
					</div>
				</div>
			</div>
		</div>

		<Lightbox
			:images="selectedVariant?.gallery ?? product.images"
			:initial-index="lightboxIndex"
			:is-open="lightboxOpen"
			@close="closeLightbox"
		/>
	</div>
</template>
